import classNames from "classnames";
import { Entry, EntrySkeletonType } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties } from "react";

import BreakpointAwareAsset from "~/components/common/breakpoint-aware-asset";
import Container from "~/components/common/container";
import Grid from "~/components/common/grid";
import { useServerSideProps } from "~/contexts/server-side-props";
import Editorials from "~/types/editorials";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import HeroPanelContent from "./hero-panel-content";
import styles from "./hero-slide.module.scss";

type Props = {
  entry: Entry<EntrySkeletonType>;
  isHeroSlider?: boolean;
  noLazy?: boolean;
  isActive?: boolean;
};

export default function HeroSlide(props: Props) {
  const router = useRouter();
  const { serverSideProps } = useServerSideProps();

  if (!isActiveEntry(props.entry)) return null;

  assertEditorialType<Editorials.HeroSlide>(props.entry, "heroSlide");

  const contentBelowHeader = serverSideProps?.page?.contentBelowHeader;
  let aspectRatioMobile = "unset";
  let aspectRatioDesktop = "unset";
  if (isActiveEntry(props.entry.fields.media)) {
    aspectRatioMobile =
      props.entry.fields.media.fields.aspectRatioMobile ?? "unset";
    aspectRatioDesktop =
      props.entry.fields.media.fields?.aspectRatioDesktop ?? "unset";
  }

  const styleAspectRatio = {
    "--hero-slide-aspect-ratio-mobile": aspectRatioMobile,
    "--hero-slide-aspect-ratio-desktop": aspectRatioDesktop,
  } as CSSProperties;

  const breakpointAssetProps = {
    entry: props.entry.fields.media,
    className: classNames(
      styles.image,
      props.entry.fields.moveMediaDown ? styles.moveMediaDown : undefined,
    ),
    fill: true,
    videoConfig: {
      muted: true,
      loop: true,
      autoPlay: true,
      controls: false,
      playsInline: true,
    },
    noLazy: props.isHeroSlider ? props.noLazy : true,
  };

  return (
    <div
      className={classNames(
        styles.container,
        contentBelowHeader ? styles.contentBelowHeader : undefined,
        aspectRatioMobile !== "unset" ? styles.autoHeightMobile : undefined,
        aspectRatioDesktop !== "unset" ? styles.autoHeightDesktop : undefined,
      )}
      aria-hidden={!props.isActive}
    >
      <div
        style={styleAspectRatio}
        className={classNames(
          styles.wrapperSlide,
          aspectRatioMobile !== "unset" ? styles.autoHeightMobile : undefined,
          aspectRatioDesktop !== "unset" ? styles.autoHeightDesktop : undefined,
        )}
      >
        {props.entry.fields.customPanel?.hidePanel ? (
          <BreakpointAwareAsset {...breakpointAssetProps} />
        ) : props.entry.fields.link &&
          isActiveEntry(props.entry.fields.link) ? (
          <Link
            href={textUtils.sanitizeContentfulUrl(
              props.entry.fields.link,
              router,
            )}
            target={
              props.entry.fields.link.fields.openOnANewTab
                ? "_blank"
                : undefined
            }
            tabIndex={props.isActive ? 0 : -1}
          >
            <BreakpointAwareAsset {...breakpointAssetProps} />
          </Link>
        ) : (
          <BreakpointAwareAsset {...breakpointAssetProps} />
        )}
        <Container className={styles.h100}>
          <Grid className={styles.h100}>
            <HeroPanelContent
              entry={props.entry}
              isActive={props.isActive}
              isHeroSlider={props.isHeroSlider}
              contentBelowHeader={contentBelowHeader}
            />
          </Grid>
        </Container>
      </div>
    </div>
  );
}
